import React, { useRef } from 'react';
import { RouteComponentProps, Redirect, Link } from 'react-router-dom';
import { Breadcrumb } from 'react-bootstrap';
import withSSR, { SSRProps } from 'utils/withSSR';
import Loading from 'components/Loading';
import ErrorBox from 'components/ErrorBox';
import store from '../../store';

import EmptyHelp from '../../components/EmptyHelp';

import Content from './Content';
import SideMenu from './SideMenu';
import NavMenu from './NavMenu';

import './style.scss';

const HelpDetail: React.FC<
    SSRProps<{
        initialData: any;
    }> &
        RouteComponentProps<{
            main: string;
            sub?: string;
        }>
> = ({ initialData = {}, ui, ...props }) => {
    const { list, detail } = initialData;
    const { main, sub } = props.match.params;
    const inputRef = useRef(null);

    const goSearch = () => {
        const value = (inputRef.current as any).value;

        if (value) {
            props.history.push(`/help/search?keyword=${encodeURIComponent(value)}`);
        }
    };

    const handleInput = e => {
        if (e.nativeEvent.keyCode === 13) {
            goSearch();
        }
    };

    function render() {
        if (props.__loading__) {
            return <Loading tip="loading..." />;
        }

        if (props.__error__) {
            return <ErrorBox error={props.__error__} onClick={props.__getData__} />;
        }

        if (!Array.isArray(list.items)) {
            return <EmptyHelp>Sorry, this content isn't available right now</EmptyHelp>;
        }

        const data = list.items.filter(item => {
            return item.link_code === main;
        });

        if (!data.length) {
            return <Redirect to={{ ...props.location, pathname: '/help' }} />;
        }

        if (!sub || (sub && data[0].children.every(item => String(item.id) !== sub))) {
            return <Redirect to={{ ...props.location, pathname: `/help/${main}/${data[0]?.children[0]?.id}` }} />;
        }

        return (
            <>
                {!ui.isTiger && (
                    <div className="help-common-top">
                        <div className="site-main">
                            <div className="d-flex">
                                <Breadcrumb className="search-breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to="/help">Help</Link>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <Link to={`/help/${main}`}>{data[0].title}</Link>
                                    </li>
                                    <Breadcrumb.Item active>
                                        {data[0].children.filter(item => String(item.id) === sub)[0].title}
                                    </Breadcrumb.Item>
                                </Breadcrumb>
                                <div className="search-input">
                                    <input
                                        className="search"
                                        placeholder="Search"
                                        ref={inputRef}
                                        onKeyPress={handleInput}
                                    />
                                    <div className="search-icon" onClick={goSearch}></div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <div className="help-main site-main">
                    <div className="help-nav-detail">
                        <NavMenu {...props} data={list} />
                        <SideMenu {...props} data={list} ui={ui} />
                        <Content {...props} data={detail} />
                    </div>
                </div>
            </>
        );
    }

    return <div className="help-detail-root">{render()}</div>;
};

export default withSSR(HelpDetail, async ({ i18n, match, currentUser }) => {
    const [list, detail] = await Promise.all([
        store.getHelpList(i18n?.language, currentUser),
        match.params.sub && store.getListDetail(match.params.sub, i18n?.language, currentUser)
    ]);

    return { initialData: { list, detail } };
});
